/**
 * A signature at full size, with the option to save it.
 *
 * The preview here is the same element the creation screen captures, so what downloads is what
 * was e-mailed — with one caveat the modal states out loud: the image is re-rendered from the
 * stored record, because the BFF never keeps the PNG itself.
 *
 * @module Pages/Signatures
 */

import { css } from '@emotion/css';
import { UTButton, UTModal } from '@group-link-one/gl-ui/utilities';
import { useRef, useState } from 'react';
import type { JSX } from 'react';
import type { EmployeeSignature } from '../SignatureList/Signatures.types';
import { downloadSignaturePng, renderSignatureBase64 } from '../../Utils/downloadSignature';
import { resendSignature } from '../../Services/Signatures.service';
import { SignaturePreview } from '../SignaturePreview/SignaturePreview';
import { useActionToast } from '@/Hooks/useActionToast';

const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-ml);
  width: 100%;
  min-width: 0;
`;

/*
  The frame lives out here, on a wrapper: the signature itself draws none, because anything on it
  is captured into the PNG that goes in the e-mail.
*/
const previewFrameStyle = css`
  border: 1px solid var(--color-border);
  border-radius: var(--radius-card);
  overflow: hidden;
`;

const noteStyle = css`
  margin: 0;
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

const footerStyle = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-sm);
`;

export interface SignatureViewModalProps {
  open: boolean;
  signature: EmployeeSignature;
  canDownload: boolean;
  /**
   * Whether this person may send the signature again.
   *
   * The action lives here rather than on the card because this is where the signature is actually
   * rendered — the BFF never stored the image, so resending has to produce it from the DOM.
   */
  canResend?: boolean;
  onClose: () => void;
  onResent?: () => void;
}

export const SignatureViewModal = ({
  open,
  signature,
  canDownload,
  canResend = false,
  onClose,
  onResent,
}: SignatureViewModalProps): JSX.Element => {
  const toast = useActionToast();
  const previewRef = useRef<HTMLDivElement>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const handleDownload = async () => {
    const element = document.getElementById(`signature-view-${signature.id}`);
    if (!element) return;

    setIsSaving(true);

    try {
      await downloadSignaturePng(element, signature.employeeName);
      toast.success('Assinatura baixada');
    } catch (error) {
      toast.failure('Não foi possível baixar a assinatura', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleResend = async () => {
    const element = document.getElementById(`signature-view-${signature.id}`);
    if (!element) return;

    setIsResending(true);

    try {
      await resendSignature(signature.id, await renderSignatureBase64(element));
      toast.success(`Assinatura reenviada para ${signature.email}`);
      onResent?.();
      onClose();
    } catch (error) {
      toast.failure('Não foi possível reenviar a assinatura', error);
    } finally {
      setIsResending(false);
    }
  };

  const content = (
    <div className={bodyStyle} ref={previewRef}>
      <div className={previewFrameStyle}>
        <SignaturePreview
          id={`signature-view-${signature.id}`}
          name={signature.employeeName}
          position={signature.position}
          phone={signature.phone}
          email={signature.email}
        />
      </div>

      <p className={noteStyle}>
        A imagem é gerada a partir do registro, no modelo atual — o BFF guarda os dados da
        assinatura, não o PNG que foi enviado.
      </p>

      <div className={footerStyle}>
        <UTButton fitContent variant='secondary' text='Fechar' onClick={onClose} />
        {canResend ? (
          <UTButton
            fitContent
            variant='secondary'
            id={`signature-resend-${signature.id}`}
            text={isResending ? 'Reenviando...' : 'Reenviar'}
            icon='Communication.Envelope'
            disabled={isResending || isSaving}
            onClick={() => {
              void handleResend();
            }}
          />
        ) : null}
        {canDownload ? (
          <UTButton
            fitContent
            id={`signature-download-${signature.id}`}
            text={isSaving ? 'Gerando...' : 'Baixar PNG'}
            icon='Arrows.TrayArrowUp'
            disabled={isSaving}
            onClick={() => {
              void handleDownload();
            }}
          />
        ) : null}
      </div>
    </div>
  );

  return (
    <UTModal
      open={open}
      onClose={onClose}
      title={signature.employeeName}
      content={content}
      maxWidth='600px'
      hasButtonClose
      position='center'
    />
  );
};
